<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { useUserStore } from '@/stores/UserStore'
import { Ref, ref } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { authApi, userApi } from '@/api/client'
import { useTheme, type Theme } from '@/composables/useTheme'
import {
    SidebarProvider,
    Sidebar,
    SidebarCollapsibleMenu,
    SidebarHeader,
    SidebarContent,
    SidebarFooter,
    SidebarGroup,
    SidebarGroupContent,
    SidebarMenu,
    SidebarMenuItem,
    SidebarMenuButton,
    SidebarMenuBadge,
    SidebarMenuSub,
    SidebarMenuSubItem,
    SidebarMenuSubButton,
    SidebarSeparator,
    SidebarInset,
    SidebarTrigger,
} from '@/components/ui/sidebar'
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuLabel,
    DropdownMenuSeparator,
    DropdownMenuItem,
    DropdownMenuRadioGroup,
    DropdownMenuRadioItem,
} from '@/components/ui/dropdown-menu'
import { Separator } from '@/components/ui/separator'
import {
    PieChart,
    MousePointerClick,
    Monitor,
    FileText,
    Headset,
    LogOut,
    ChevronsUpDown,
    Sun,
    Moon,
    SunMoon,
    ChartLine,
} from 'lucide-vue-next'
import type { User } from '@/types'

const userStore = useUserStore()
const router = useRouter()
const route = useRoute()
const { t } = useI18n()
const { theme, setTheme } = useTheme()

type NotificationResponse = {
    notifications: number
}

let data: Ref<NotificationResponse | null> = ref(null)

const logout = async () => {
    await authApi.logout()
    userStore.logout()
    await router.push({ name: 'login' })
}

const loadNotifications = async () => {
    try {
        const notificationResponse = await userApi.notifications()
        data.value = notificationResponse.data
    } catch {}
}

loadNotifications()

/**
 * The profile is cached at login, so a flag switched on afterwards (the Insights page,
 * REC-854) would stay invisible until the customer logged in again. One refresh per
 * app load picks it up; a failure keeps the cached profile.
 */
const refreshProfile = async () => {
    try {
        const userResponse = await authApi.user()
        userStore.login(userResponse.data as User)
    } catch {}
}

refreshProfile()

const themeOptions: { value: Theme; label: string; icon: typeof Sun }[] = [
    { value: 'light', label: 'Light', icon: Sun },
    { value: 'dark', label: 'Dark', icon: Moon },
    { value: 'auto', label: 'System', icon: SunMoon },
]

const isRouteActive = (name: string) => {
    return route.name === name || route.name?.toString().startsWith(name + '.')
}
</script>

<template>
    <SidebarProvider :default-open="true">
        <Sidebar collapsible="icon">
            <SidebarHeader>
                <SidebarMenu>
                    <SidebarMenuItem class="flex items-center">
                        <SidebarMenuButton
                            size="lg"
                            as="span"
                            class="hover:bg-transparent active:bg-transparent cursor-default"
                        >
                            <div
                                class="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs font-bold"
                            >
                                {{
                                    (
                                        userStore.configuration?.store_name ||
                                        userStore.profile?.name ||
                                        'R'
                                    )
                                        .charAt(0)
                                        .toUpperCase()
                                }}
                            </div>
                            <div class="grid flex-1 text-left text-sm leading-tight">
                                <span class="truncate font-semibold">
                                    {{
                                        userStore.configuration?.store_name ||
                                        userStore.profile?.name
                                    }}
                                </span>
                                <span class="truncate text-xs text-muted-foreground">
                                    RecommerceIQ
                                </span>
                            </div>
                        </SidebarMenuButton>
                        <SidebarTrigger
                            class="ml-auto hidden size-6 shrink-0 md:inline-flex [&>svg]:size-3.5 group-data-[collapsible=icon]:hidden"
                        />
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarHeader>

            <SidebarContent>
                <SidebarGroup>
                    <SidebarGroupContent>
                        <SidebarMenu>
                            <!-- Catalog -->
                            <SidebarMenuItem
                                v-if="userStore.configuration?.show_catalog"
                            >
                                <SidebarMenuButton
                                    as-child
                                    :is-active="isRouteActive('dashboard.catalog')"
                                    :tooltip="t('sidebar.catalog')"
                                >
                                    <router-link to="/dashboard/catalog">
                                        <FileText />
                                        <span>{{ t('sidebar.catalog') }}</span>
                                    </router-link>
                                </SidebarMenuButton>
                                <SidebarMenuBadge
                                    v-if="
                                        data?.notifications !== undefined &&
                                        data?.notifications > 0
                                    "
                                >
                                    <span
                                        class="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive text-[10px] font-bold text-destructive-foreground"
                                    >
                                        {{ data?.notifications }}
                                    </span>
                                </SidebarMenuBadge>
                            </SidebarMenuItem>

                            <!-- Insights -->
                            <SidebarMenuItem v-if="userStore.configuration?.market_page_enabled">
                                <SidebarMenuButton
                                    as-child
                                    :is-active="isRouteActive('dashboard.insights')"
                                    :tooltip="t('sidebar.insights')"
                                >
                                    <router-link :to="{ name: 'dashboard.insights' }">
                                        <ChartLine />
                                        <span>{{ t('sidebar.insights') }}</span>
                                    </router-link>
                                </SidebarMenuButton>
                            </SidebarMenuItem>

                            <!-- Prices (collapsible) -->
                            <SidebarCollapsibleMenu
                                v-if="userStore.profile"
                                :icon="PieChart"
                                :label="t('sidebar.prices.title')"
                                :default-open="isRouteActive('dashboard.prices')"
                            >
                                <template #collapsible>
                                    <SidebarMenuSub>
                                        <SidebarMenuSubItem
                                            v-for="category in userStore.profile!.categories"
                                            :key="category.id"
                                        >
                                            <SidebarMenuSubButton
                                                as-child
                                                :is-active="
                                                    route.name ===
                                                        'dashboard.prices.category' &&
                                                    String(route.params.id) ===
                                                        String(category.id)
                                                "
                                            >
                                                <router-link
                                                    :to="{
                                                        name: 'dashboard.prices.category',
                                                        params: { id: category.id },
                                                    }"
                                                >
                                                    <span>{{
                                                        t(
                                                            `sidebar.prices.${category.name.toLowerCase()}`,
                                                        )
                                                    }}</span>
                                                </router-link>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    </SidebarMenuSub>
                                </template>
                                <template #items>
                                    <router-link
                                        v-for="category in userStore.profile!.categories"
                                        :key="category.id"
                                        :to="{
                                            name: 'dashboard.prices.category',
                                            params: { id: category.id },
                                        }"
                                        class="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                        :class="{
                                            'bg-sidebar-accent text-sidebar-accent-foreground font-medium':
                                                route.name === 'dashboard.prices.category' &&
                                                String(route.params.id) === String(category.id),
                                        }"
                                    >
                                        {{
                                            t(
                                                `sidebar.prices.${category.name.toLowerCase()}`,
                                            )
                                        }}
                                    </router-link>
                                </template>
                            </SidebarCollapsibleMenu>

                            <!-- Strategies (collapsible) -->
                            <SidebarCollapsibleMenu
                                :icon="MousePointerClick"
                                :label="t('sidebar.strategies.title')"
                                :default-open="isRouteActive('dashboard.strategies')"
                            >
                                <template #collapsible>
                                    <SidebarMenuSub>
                                        <SidebarMenuSubItem
                                            v-if="userStore.extra?.resell_enabled"
                                        >
                                            <SidebarMenuSubButton
                                                as-child
                                                :is-active="
                                                    route.name ===
                                                    'dashboard.strategies.resell'
                                                "
                                            >
                                                <router-link
                                                    :to="{
                                                        name: 'dashboard.strategies.resell',
                                                    }"
                                                >
                                                    <span>{{
                                                        t('sidebar.strategies.resell')
                                                    }}</span>
                                                </router-link>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                        <SidebarMenuSubItem
                                            v-if="userStore.extra?.buyback_enabled"
                                        >
                                            <SidebarMenuSubButton
                                                as-child
                                                :is-active="
                                                    route.name ===
                                                    'dashboard.strategies.buyback'
                                                "
                                            >
                                                <router-link
                                                    :to="{
                                                        name: 'dashboard.strategies.buyback',
                                                    }"
                                                >
                                                    <span>{{
                                                        t('sidebar.strategies.buyback')
                                                    }}</span>
                                                </router-link>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    </SidebarMenuSub>
                                </template>
                                <template #items>
                                    <router-link
                                        v-if="userStore.extra?.resell_enabled"
                                        :to="{ name: 'dashboard.strategies.resell' }"
                                        class="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                        :class="{
                                            'bg-sidebar-accent text-sidebar-accent-foreground font-medium':
                                                route.name === 'dashboard.strategies.resell',
                                        }"
                                    >
                                        {{ t('sidebar.strategies.resell') }}
                                    </router-link>
                                    <router-link
                                        v-if="userStore.extra?.buyback_enabled"
                                        :to="{ name: 'dashboard.strategies.buyback' }"
                                        class="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                        :class="{
                                            'bg-sidebar-accent text-sidebar-accent-foreground font-medium':
                                                route.name === 'dashboard.strategies.buyback',
                                        }"
                                    >
                                        {{ t('sidebar.strategies.buyback') }}
                                    </router-link>
                                </template>
                            </SidebarCollapsibleMenu>

                            <!-- Competitors (collapsible) -->
                            <SidebarCollapsibleMenu
                                v-if="
                                    userStore.profile?.vendors &&
                                    userStore.profile?.vendors.length > 0
                                "
                                :icon="Monitor"
                                :label="t('sidebar.competitors')"
                                :default-open="isRouteActive('dashboard.competitors')"
                            >
                                <template #collapsible>
                                    <SidebarMenuSub>
                                        <SidebarMenuSubItem
                                            v-for="vendor in userStore.profile?.vendors"
                                            :key="vendor.id"
                                        >
                                            <SidebarMenuSubButton
                                                as-child
                                                :is-active="
                                                    route.name ===
                                                        'dashboard.competitors.detail' &&
                                                    String(route.params.id) ===
                                                        String(vendor.id)
                                                "
                                            >
                                                <router-link
                                                    :to="{
                                                        name: 'dashboard.competitors.detail',
                                                        params: { id: vendor.id },
                                                    }"
                                                >
                                                    {{ vendor.name }} {{ vendor.country.toUpperCase() }}
                                                </router-link>
                                            </SidebarMenuSubButton>
                                        </SidebarMenuSubItem>
                                    </SidebarMenuSub>
                                </template>
                                <template #items>
                                    <router-link
                                        v-for="vendor in userStore.profile?.vendors"
                                        :key="vendor.id"
                                        :to="{
                                            name: 'dashboard.competitors.detail',
                                            params: { id: vendor.id },
                                        }"
                                        class="flex w-full items-center rounded-sm px-2 py-1.5 text-sm hover:bg-sidebar-accent hover:text-sidebar-accent-foreground"
                                        :class="{
                                            'bg-sidebar-accent text-sidebar-accent-foreground font-medium':
                                                route.name === 'dashboard.competitors.detail' &&
                                                String(route.params.id) === String(vendor.id),
                                        }"
                                    >
                                        {{ vendor.name }} {{ vendor.country.toUpperCase() }}
                                    </router-link>
                                </template>
                            </SidebarCollapsibleMenu>
                        </SidebarMenu>
                    </SidebarGroupContent>
                </SidebarGroup>
            </SidebarContent>

            <SidebarFooter>
                <SidebarMenu>
                    <!-- Support -->
                    <SidebarMenuItem>
                        <SidebarMenuButton
                            as="a"
                            href="mailto:support@recommerceiq.com"
                            :tooltip="t('sidebar.support')"
                        >
                            <Headset />
                            <span>{{ t('sidebar.support') }}</span>
                        </SidebarMenuButton>
                    </SidebarMenuItem>

                </SidebarMenu>

                <SidebarSeparator />

                <!-- User menu -->
                <SidebarMenu>
                    <SidebarMenuItem>
                        <DropdownMenu>
                            <DropdownMenuTrigger as-child>
                                <SidebarMenuButton
                                    size="lg"
                                    class="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
                                >
                                    <div
                                        class="bg-sidebar-primary text-sidebar-primary-foreground flex aspect-square size-8 items-center justify-center rounded-lg text-xs font-bold"
                                    >
                                        {{
                                            (userStore.profile?.name || 'U')
                                                .charAt(0)
                                                .toUpperCase()
                                        }}
                                    </div>
                                    <div class="grid flex-1 text-left text-sm leading-tight">
                                        <span class="truncate font-semibold">
                                            {{ userStore.profile?.name }}
                                        </span>
                                        <span
                                            class="truncate text-xs text-muted-foreground"
                                        >
                                            {{ userStore.profile?.email }}
                                        </span>
                                    </div>
                                    <ChevronsUpDown class="ml-auto size-4" />
                                </SidebarMenuButton>
                            </DropdownMenuTrigger>

                            <DropdownMenuContent
                                class="w-(--reka-popper-anchor-width) min-w-56 rounded-lg"
                                side="top"
                                align="start"
                                :side-offset="4"
                            >
                                <DropdownMenuLabel class="text-xs text-muted-foreground">
                                    Theme
                                </DropdownMenuLabel>
                                <DropdownMenuRadioGroup
                                    :model-value="theme"
                                    @update:model-value="
                                        setTheme($event as Theme)
                                    "
                                >
                                    <DropdownMenuRadioItem
                                        v-for="option in themeOptions"
                                        :key="option.value"
                                        :value="option.value"
                                    >
                                        <component :is="option.icon" class="mr-2 size-4" />
                                        {{ option.label }}
                                    </DropdownMenuRadioItem>
                                </DropdownMenuRadioGroup>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem @click="logout">
                                    <LogOut class="mr-2 size-4" />
                                    {{ t('header.logout') }}
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </SidebarMenuItem>
                </SidebarMenu>
            </SidebarFooter>
        </Sidebar>

        <SidebarInset>
            <header class="flex h-12 shrink-0 items-center gap-2 border-b px-4 md:hidden">
                <SidebarTrigger class="-ml-1 size-6 [&>svg]:size-3.5" />
                <Separator orientation="vertical" class="mr-2 h-4" />
                <span class="text-sm font-medium">
                    {{ userStore.configuration?.store_name || userStore.profile?.name }}
                </span>
            </header>

            <div class="relative min-w-0 flex-1 overflow-x-auto p-6 sm:p-8">
                <SidebarTrigger
                    class="absolute top-2 left-2 hidden size-6 group-data-[state=collapsed]/sidebar-wrapper:md:inline-flex [&>svg]:size-3.5"
                />
                <router-view />
            </div>
        </SidebarInset>
    </SidebarProvider>
</template>
